import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "./supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [profile,     setProfile]     = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading,     setLoading]     = useState(true);
  const [authError,   setAuthError]   = useState("");

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) loadProfile(session.user);
      else setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) loadProfile(session.user);
      else { setUser(null); setProfile(null); setPermissions({}); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (authUser) => {
    setUser(authUser);
    // Load profile — check both id and auth_id for registered users
    let { data: prof } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .single();

    // If not found by id, try auth_id (for users who registered via WhatsApp link)
    if (!prof) {
      const { data: prof2 } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_id', authUser.id)
        .single();
      prof = prof2;
    }

    if (prof) {
      setProfile(prof);
      // Update last_seen — this is how "Has Account" is tracked in the People Directory
      supabase.from('profiles').update({ last_seen: new Date().toISOString() }).eq('id', prof.id).then(() => {});
      // Load permissions for this role
      const { data: perms } = await supabase
        .from('role_permissions')
        .select('module, access')
        .eq('role', prof.role);

      const permMap = {};
      (perms || []).forEach(p => { permMap[p.module] = p.access; });
      setPermissions(permMap);
    } else {
      // No profile found — could be a Google OAuth user without a pre-created profile.
      // Sign them out so they see a clear message rather than an infinite loop.
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      setAuthError("No account found for this email. New here? Use \"Sign up\" to join — HQ will review your details.");
    }
    setLoading(false);
  };

  const signIn = async (email, password) => {
    setAuthError("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Check if user can access a module
  const can = (module) => {
    const access = permissions[module];
    return access && access !== 'none';
  };

  // Get access level for a module: 'full' | 'view' | 'limited' | 'none'
  const accessLevel = (module) => permissions[module] || 'none';

  // Check if user has full access to a module
  const canEdit = (module) => permissions[module] === 'full';

  // Check if user can use AI tools in a given module
  // Rules: superuser = always | media = content module only | dreams = everyone
  const canUseAI = (module) => {
    const role = profile?.role;
    if (role === 'superuser') return true;
    if (module === 'dreams') return true;  // everyone can use AI in dreams
    if (module === 'content' && role === 'media') return true; // media team for content
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, profile, permissions, loading, authError, signIn, signOut, can, canEdit, accessLevel, canUseAI }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
